import React, { useContext } from "react";
import moment from "moment";
import { IntlContext } from "context";
import type { IntlShape } from "context/Intl";
import { AdInterface } from "types/ad";
import Image from "components/File/Image";
import { BadgeCheck } from "lucide-react";

const formatMembershipDuration = (intl: IntlShape, date1: moment.Moment, date2: moment.Moment): string => {
    const diffInMilliseconds = Math.abs(date2.valueOf() - date1.valueOf());
    const diff = moment.duration(diffInMilliseconds);

    const years = diff.years();
    diff.subtract(years, "years");

    const months = diff.months();
    diff.subtract(months, "months");

    const weeks = diff.weeks();
    diff.subtract(weeks, "weeks");

    const days = diff.days();
    diff.subtract(days, "days");

    const hours = diff.hours();

    if (years > 0) return intl.formatMessage("ad.view.time.years", { count: years });
    if (months > 0) return intl.formatMessage("ad.view.time.months", { count: months });
    if (weeks > 0) return intl.formatMessage("ad.view.time.weeks", { count: weeks });
    if (days > 0) return intl.formatMessage("ad.view.time.days", { count: days });
    return intl.formatMessage("ad.view.time.hours", { count: hours });
};

type Props = {
    ad: AdInterface;
};

const AdAdvertiserCard: React.FC<Props> = ({ ad }) => {
    const intl = useContext(IntlContext);
    const isVerified = ad.client.dataleon_status === "approved";

    return (
        <div className="bg-white rounded-[32px] border border-[#f1f5f9] drop-shadow-[0px_10px_20px_rgba(0,0,0,0.04)] pt-[33px] px-[33px] pb-[33px] flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <div className="w-1 h-4 bg-primary rounded-full shrink-0" />
                <h2 className="text-[14px] font-black tracking-[2.1px] uppercase text-foreground">
                    {intl.formatMessage("ad.view.about_advertiser")}
                </h2>
            </div>

            <div className="flex items-start gap-6">
                <div className="relative shrink-0">
                    <div className="w-20 h-20 rounded-[24px] border-2 border-[#f1f5f9] bg-gradient-to-br from-indigo-200 to-purple-200 flex items-center justify-center text-2xl overflow-hidden p-0.5">
                        {ad.client.picture_id ? (
                            <Image id={ad.client.picture_id} className="w-full h-full object-cover rounded-[22px]" />
                        ) : (
                            <span>
                                {ad.client.first_name?.charAt(0)}
                                {ad.client.last_name?.charAt(0)}
                            </span>
                        )}
                    </div>
                    {isVerified && (
                        <span className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-primary border-2 border-white drop-shadow-[0px_4px_3px_rgba(0,0,0,0.1)] flex items-center justify-center text-white text-xs">
                            <BadgeCheck className="w-4 h-4" />
                        </span>
                    )}
                </div>
                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                        <span className="text-[22px] font-black text-foreground leading-[33px]">
                            {ad.client.first_name} {ad.client.last_name?.charAt(0)}.
                        </span>
                        {isVerified && (
                            <span className="bg-secondary text-primary text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-[1px]">
                                {intl.formatMessage("ad.view.verified_badge")}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 text-[13px] text-[#90a1b9] font-medium mb-3">
                        {intl.formatMessage("ad.view.member_since", {
                            duration: formatMembershipDuration(intl, moment(ad.client.created_at), moment()),
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdAdvertiserCard;
